import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { HeaderPro } from '../../components/HeaderPro';
import { Footer } from '../../components/Footer';
import { Pagination } from '../../components/Pagination';
import {
  BookingsFilterBar,
  EMPTY_BOOKINGS_FILTER,
  type BookingsFilterValue,
} from '../../components/BookingsFilterBar';
import {
  acceptProBooking,
  cancelProBooking,
  completeProBooking,
  listProBookings,
  rejectProBooking,
  startProBooking,
  type ProBookingRow,
} from '../../../lib/professional-api';
import { apiErrorMessage } from '../../../lib/api';
import { toast } from '../../../lib/toast';

// Mapping the backend status enum onto the customer-facing tabs the
// pro mock UI already used, plus a new "Pending" bucket for bookings
// awaiting the pro's accept/reject decision. CONFIRMED + IN_PROGRESS
// are both "Upcoming" (work hasn't happened yet); CANCELLED_BY_* and
// REFUNDED collapse into one Cancelled bucket; FAILED is a payment
// failure and we hide it from the pro view entirely.
type FilterTab =
  | 'All'
  | 'Pending'
  | 'Upcoming'
  | 'In progress'
  | 'Completed'
  | 'Cancelled';

const fullName = (first: string | null, last: string | null) =>
  [first, last].filter(Boolean).join(' ') || '—';

const TAB_STATUSES: Record<FilterTab, string[] | null> = {
  All: null,
  Pending: ['PENDING_PROFESSIONAL_CONFIRM'],
  Upcoming: ['CONFIRMED'],
  'In progress': ['IN_PROGRESS'],
  Completed: ['COMPLETED'],
  Cancelled: [
    'CANCELLED_BY_CUSTOMER',
    'CANCELLED_BY_PRO',
    'CANCELLED_BY_ADMIN',
    'REFUNDED',
  ],
};

const STATUS_LABEL: Record<string, string> = {
  PENDING_PROFESSIONAL_CONFIRM: 'Awaiting your confirmation',
  CONFIRMED: 'Booked',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED_BY_CUSTOMER: 'Cancelled',
  CANCELLED_BY_PRO: 'Cancelled by you',
  CANCELLED_BY_ADMIN: 'Cancelled — admin',
  REFUNDED: 'Refunded',
};

const STATUS_STYLES: Record<string, string> = {
  PENDING_PROFESSIONAL_CONFIRM: 'bg-[#fff7e6] text-[#9a6700] border border-[#fde2a1]',
  CONFIRMED: 'bg-[#f5f0e6] text-[#8e2a2f]',
  IN_PROGRESS: 'bg-[#8e2a2f] text-white',
  COMPLETED: 'bg-[#5a5c55] text-white',
  CANCELLED_BY_CUSTOMER: 'bg-[#e8cd96] text-[#8e2a2f]',
  CANCELLED_BY_PRO: 'bg-[#e8cd96] text-[#8e2a2f]',
  CANCELLED_BY_ADMIN: 'bg-[#e8cd96] text-[#8e2a2f]',
  REFUNDED: 'bg-[#e8cd96] text-[#8e2a2f]',
};

function formatDateGroup(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatDisplayDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h${m}` : `${h}h`;
}

const PAGE_SIZE = 10;

export default function BookingsPage() {
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState<FilterTab>('All');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [rows, setRows] = useState<ProBookingRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<BookingsFilterValue>(
    EMPTY_BOOKINGS_FILTER,
  );

  // Server-paginated. Tabs that map to a single status hit the
  // backend's `status=` filter; tabs that span multiple statuses
  // (currently only "Cancelled") get one fetch per status and the
  // results are merged. limit=10 in all cases so the API call stays
  // small. Backend default-sorts created_at DESC so the freshest
  // booking always shows at the top.
  const load = async () => {
    setLoading(true);
    try {
      const statuses = TAB_STATUSES[selectedFilter];
      const baseParams = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        q: filters.q.trim() || undefined,
        service_id: filters.services[0],
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
      };
      if (!statuses) {
        const res = await listProBookings(baseParams);
        setRows(res.data);
        setTotalRows(res.total_count ?? res.data.length);
      } else if (statuses.length === 1) {
        const res = await listProBookings({
          ...baseParams,
          status: statuses[0],
        });
        setRows(res.data);
        setTotalRows(res.total_count ?? res.data.length);
      } else {
        // Multi-status bucket (Cancelled) — fan out one fetch per
        // status with the same offset / limit and concatenate. Total
        // is the sum of per-status counts; pagination is approximate
        // for multi-status buckets but works fine for "Cancelled".
        const results = await Promise.all(
          statuses.map((s) =>
            listProBookings({ ...baseParams, status: s }),
          ),
        );
        const merged = results.flatMap((r) => r.data);
        merged.sort((a, b) => {
          const at = new Date(a.created_at ?? a.scheduled_start).getTime();
          const bt = new Date(b.created_at ?? b.scheduled_start).getTime();
          return bt - at;
        });
        setRows(merged.slice(0, PAGE_SIZE));
        setTotalRows(
          results.reduce((acc, r) => acc + (r.total_count ?? r.data.length), 0),
        );
      }
    } catch (err: any) {
      toast.error(
        apiErrorMessage(err, 'Failed to load'),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [selectedFilter, filters]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, page, filters]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function handleAction(
    action: 'start' | 'complete' | 'cancel' | 'accept' | 'reject',
    row: ProBookingRow,
  ) {
    setActingId(row.id);
    setOpenMenuId(null);
    try {
      if (action === 'start') {
        await startProBooking(row.id);
      } else if (action === 'complete') {
        await completeProBooking(row.id);
      } else if (action === 'accept') {
        await acceptProBooking(row.id);
        toast.success('Booking accepted.');
      } else if (action === 'reject') {
        await rejectProBooking(row.id, null);
        toast.success('Booking rejected. The customer will be refunded.');
      } else {
        await cancelProBooking(row.id);
      }
      await load();
    } catch (err: any) {
      toast.error(
        apiErrorMessage(err, 'Action failed.'),
      );
    } finally {
      setActingId(null);
    }
  }

  // Search / filter / pagination are all server-side now, so `rows`
  // is the exact slice to render. Group by date for the visual
  // headers — the order in `rows` is preserved (backend sorts
  // created_at DESC by default).
  const serviceOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows) {
      if (r.service?.id && r.service.name && !seen.has(r.service.id)) {
        seen.set(r.service.id, r.service.name);
      }
    }
    return Array.from(seen.entries()).map(([id, label]) => ({ id, label }));
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const groupedBookings = useMemo(() => {
    // Group by the BOOKING date (when the customer made the booking)
    // since the list is sorted created_at DESC.
    const out: Record<string, ProBookingRow[]> = {};
    for (const r of rows) {
      const anchor = r.created_at ?? r.scheduled_start;
      const key = formatDateGroup(anchor);
      if (!out[key]) out[key] = [];
      out[key].push(r);
    }
    return out;
  }, [rows]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <HeaderPro />

      <main className="flex-1 pb-16">
        <div className="max-w-[1140px] mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] leading-[32px] tracking-[-0.094px]">
              Your bookings
            </h1>
          </div>

          <BookingsFilterBar
            value={filters}
            onChange={setFilters}
            serviceOptions={serviceOptions}
            searchPlaceholder="Search by service, customer name or reference…"
          />

          {/* Filter Tabs */}
          <div className="flex flex-nowrap overflow-x-auto gap-2 mb-8 -mx-4 px-4 pb-2 scrollbar-hide md:flex-wrap md:overflow-visible md:mx-0 md:px-0 md:pb-0">
            {(['All', 'Pending', 'Upcoming', 'In progress', 'Completed', 'Cancelled'] as FilterTab[]).map(
              (filter) => (
                <button
                  key={filter}
                  onClick={() => setSelectedFilter(filter)}
                  className={`px-6 py-2.5 rounded-[8px] font-['DM_Sans',sans-serif] font-bold text-[14px] transition-colors whitespace-nowrap shrink-0 ${
                    selectedFilter === filter
                      ? 'bg-white border border-[#ede8e9] text-[#3a3335] shadow-sm'
                      : 'bg-transparent border border-transparent text-[#908e8e] hover:text-[#3a3335]'
                  }`}
                  style={{ fontVariationSettings: "'opsz' 14" }}
                >
                  {filter}
                </button>
              ),
            )}
          </div>

          {/* Bookings List */}
          {loading ? (
            <p className="text-sm text-[#908e8e]">Loading bookings…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-[#908e8e]">
              {rows.length === 0
                ? selectedFilter === 'All'
                  ? "You don't have any bookings yet."
                  : `No ${selectedFilter.toLowerCase()} bookings.`
                : 'No bookings match your search or filters.'}
            </p>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedBookings).map(([dateGroup, dayBookings]) => (
                <div key={dateGroup}>
                  <h2 className="font-['DM_Sans',sans-serif] font-normal text-[#3a3335] text-[14px] mb-4">
                    {dateGroup}
                  </h2>
                  <div className="space-y-3">
                    {dayBookings.map((booking) => {
                      const statusLabel =
                        STATUS_LABEL[booking.status] ?? booking.status;
                      const statusStyle =
                        STATUS_STYLES[booking.status] ??
                        'bg-gray-100 text-gray-600';
                      const isMenuOpen = openMenuId === booking.id;
                      const canAcceptOrReject =
                        booking.status === 'PENDING_PROFESSIONAL_CONFIRM';
                      const canStart = booking.status === 'CONFIRMED';
                      const canComplete = booking.status === 'IN_PROGRESS';
                      const canCancel =
                        booking.status === 'CONFIRMED' ||
                        booking.status === 'IN_PROGRESS';
                      const acting = actingId === booking.id;

                      return (
                        <div
                          key={booking.id}
                          onClick={() =>
                            navigate(`/professional/bookings/${booking.id}`)
                          }
                          className="flex flex-col md:flex-row md:items-center gap-4 md:gap-4 p-4 rounded-[8px] hover:bg-[#fcfaf7] transition-colors border border-transparent hover:border-[#ede8e9] cursor-pointer"
                        >
                          <div className="flex flex-row items-start justify-between md:contents">
                            <div className="flex items-center gap-3 md:w-[200px]">
                              <div className="w-[40px] h-[40px] bg-[#f9f9f9] rounded-[8px] flex items-center justify-center shrink-0">
                                <span className="material-icons text-[#908e8e] text-[18px]">
                                  event
                                </span>
                              </div>
                              <div>
                                <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] whitespace-nowrap">
                                  {formatDisplayDate(booking.scheduled_start)}
                                </p>
                                <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
                                  {formatTime(booking.scheduled_start)} ·{' '}
                                  {formatDuration(booking.duration_minutes)}
                                </p>
                              </div>
                            </div>

                            <div className="flex-1 text-right md:text-left">
                              <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] mb-1">
                                {booking.service?.name ?? '—'}
                              </p>
                              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
                                {fullName(
                                  booking.customer?.first_name ?? null,
                                  booking.customer?.last_name ?? null,
                                )}
                                {booking.city ? ` · ${booking.city}` : ''}
                              </p>
                              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] mt-1">
                                {booking.reference}
                              </p>
                              {booking.created_at ? (
                                <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[11px] mt-1">
                                  Booked on {formatDisplayDate(booking.created_at)}
                                </p>
                              ) : null}
                            </div>
                          </div>

                          <div
                            className="flex items-center gap-3 md:justify-end relative"
                            ref={isMenuOpen ? menuRef : undefined}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span
                              className={`px-4 py-2 rounded-[8px] font-['DM_Sans',sans-serif] font-bold text-[12px] whitespace-nowrap ${statusStyle}`}
                            >
                              {statusLabel}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuId(isMenuOpen ? null : booking.id);
                              }}
                              disabled={acting}
                              className="w-[36px] h-[36px] bg-[#f9f9f9] rounded-[8px] flex items-center justify-center hover:bg-[#f0f0f0] transition-colors disabled:opacity-50"
                            >
                              <span className="material-icons text-[#908e8e] text-[18px]">
                                {acting ? 'autorenew' : 'more_horiz'}
                              </span>
                            </button>
                            {isMenuOpen ? (
                              <div className="absolute right-0 top-[44px] w-[220px] bg-white border border-[#ede8e9] rounded-[12px] shadow-2xl z-50 py-2">
                                <div className="px-4 py-2 border-b border-[#ede8e9]">
                                  <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[13px]">
                                    {booking.service?.name ?? '—'}
                                  </p>
                                  <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px]">
                                    {formatDisplayDate(booking.scheduled_start)} ·{' '}
                                    {formatTime(booking.scheduled_start)}
                                  </p>
                                </div>
                                <div className="py-1">
                                  <MenuItem
                                    icon="visibility"
                                    label="View details"
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      navigate(
                                        `/professional/bookings/${booking.id}`,
                                      );
                                    }}
                                  />
                                  {canAcceptOrReject ? (
                                    <>
                                      <MenuItem
                                        icon="check"
                                        label="Accept booking"
                                        onClick={() =>
                                          handleAction('accept', booking)
                                        }
                                      />
                                      <MenuItem
                                        icon="close"
                                        label="Reject booking"
                                        danger
                                        onClick={() => {
                                          if (
                                            confirm(
                                              'Reject this booking? The customer will be fully refunded.',
                                            )
                                          ) {
                                            handleAction('reject', booking);
                                          }
                                        }}
                                      />
                                    </>
                                  ) : null}
                                  {canStart ? (
                                    <MenuItem
                                      icon="play_arrow"
                                      label="Mark as in progress"
                                      onClick={() =>
                                        handleAction('start', booking)
                                      }
                                    />
                                  ) : null}
                                      {canComplete ? (
                                        <MenuItem
                                          icon="check_circle"
                                          label="Mark as completed"
                                          onClick={() =>
                                            handleAction('complete', booking)
                                          }
                                        />
                                      ) : null}
                                      {canCancel ? (
                                        <MenuItem
                                          icon="event_busy"
                                          label="Cancel booking"
                                          danger
                                          onClick={() => {
                                            if (
                                              confirm(
                                                'Cancel this booking? The customer gets a full refund (pro cancellations always refund 100%).',
                                              )
                                            ) {
                                              handleAction('cancel', booking);
                                            }
                                          }}
                                        />
                                      ) : null}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && totalPages > 1 ? (
            <div className="flex justify-center mt-6">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          ) : null}
        </div>
      </main>

      <Footer />
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#fcfaf7] transition-colors text-left ${
        danger ? 'text-[#8e2a2f]' : 'text-[#3a3335]'
      }`}
    >
      <span className="material-icons text-[18px]">{icon}</span>
      <span className="font-['DM_Sans',sans-serif] font-medium text-[13px]">
        {label}
      </span>
    </button>
  );
}
